import {
  Component,
  OnInit,
  input,
  output,
  inject,
  ChangeDetectionStrategy,
  effect,
  untracked,
} from "@angular/core";
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from "@angular/forms";
import { TableFieldTypeIcons, TableFieldTypes } from "../../../locale/consts.en";
import {
  FormTableAggregateType,
  TableAggregateType,
  TableFieldCondition,
  TableFieldType,
  TableItem,
  TableItemForm,
} from "./table-field.model";
import { MasterDataService } from "../../master-data/master-data-service";
import { Observable } from "rxjs";
import {
  DECIMAL_PATTERN,
  DEVICE_ATTRIBUTE_COLUMN_PREFIX,
  INTEGER_PATTERN,
  ProductAttributeScope,
  ProductAttributeType,
  ProductCategory,
} from "../../master-data/master-data.model";
import { FormTableType } from "../forms.model";
import { MatSelectChange } from "@angular/material/select";
import { FormsService } from "../forms.service";
import { first } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import {
  TableFieldConditionDialogComponent,
  TableFieldConditionDialogData,
} from "./table-filed-condition-dialog/table-field-condition-dialog.component";
import {
  DropdownEditValuesDialogComponent,
  DropdownEditValuesDialogData,
  DropdownValueItem,
} from "src/app/forms/table-field/dropdown-edit-values-dialog/dropdown-edit-values-dialog.component";
import {
  ExpressionColumnInfo,
  ExpressionErrorStateMatcher,
  expressionSyntaxValidator,
  validateExpression,
} from "./expression-validator";

@Component({
  selector: "app-table-field",
  templateUrl: "./table-field.component.html",
  styleUrls: ["./table-field.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TableFieldComponent implements OnInit {
  private masterDataService = inject(MasterDataService);
  private fb = inject(FormBuilder);
  private dialog = inject(MatDialog);
  formsService = inject(FormsService);

  readonly itemForm = input<FormGroup>(undefined);
  readonly index = input<number>(undefined);
  readonly tableType = input<FormTableType>(undefined);
  /** All product categories - their numeric attributes can be referenced in
   *  expressions of a table that has a device (deviceProductUuid) column. */
  readonly allProductCategories = input<ProductCategory[]>([]);
  readonly deleteItem = output<number>();

  inputTypes = TableFieldTypes;
  inputTypeIcons = TableFieldTypeIcons;
  TableFieldTypeT = TableFieldType;
  TableAggregateT = TableAggregateType;
  FormTableT = FormTableType;
  selectedType;

  productCategoriesMap$: Observable<Map<string, string>>;
  productCategories: ProductCategory[] = [];

  inputTmp = "";

  public expForm: FormGroup = this.fb.group({
    expression: [""],
  });
  expressionErrorMatcher = new ExpressionErrorStateMatcher();

  constructor() {
    // Categories arrive asynchronously - re-check the expression once they
    // do, so attribute references stop being flagged as unknown.
    effect(() => {
      this.allProductCategories();
      untracked(() => {
        if (this.itemForm()) {
          this.expForm.controls.expression.updateValueAndValidity();
          this.itemForm().updateValueAndValidity();
        }
      });
    });
  }

  ngOnInit(): void {
    if (this.itemForm().controls.columnKey.value === "productCategoryUuid") {
      /// read categories only in case of category column
      this.productCategoriesMap$ = this.masterDataService.productCategoriesMap$;
      this.masterDataService
        .getProductCategories()
        .pipe(first())
        .subscribe({
          next: (categories) => {
            this.productCategories = categories;
          },
        });
    }
    this.itemFormSetValues();
    this.itemFormSetValidator();
    this.itemFormDisableControls();
    this.expForm.controls.expression.setValidators(
      expressionSyntaxValidator(() => this.availableColumns()),
    );
    this.expForm.controls.expression.updateValueAndValidity();
  }

  /** Sibling columns in this table (plus product attributes, see
   *  productAttributeColumns), for the expression validator. */
  private availableColumns(): ExpressionColumnInfo[] {
    const columns = this.itemForm().parent;
    if (!(columns instanceof FormArray)) {
      return [];
    }
    const tableColumns = columns.controls
      .map((c): ExpressionColumnInfo => {
        const g = c as FormGroup;
        const coverage = this.dropdownNumericCoverage(g);
        return {
          key: g.controls["columnKey"]?.value as string,
          type: +g.controls["type"]?.value as TableFieldType,
          hasNumericValueMap: coverage.hasAny,
          allValuesNumeric: coverage.allMapped,
        };
      })
      .filter((col): col is ExpressionColumnInfo => !!col.key);
    return [...tableColumns, ...this.productAttributeColumns(tableColumns)];
  }

  /** Numeric (INTEGER/DECIMAL) product category attributes, usable as
   *  variables when the table has a deviceProductUuid column - the mobile app
   *  binds the selected product's numeric attributes by key
   *  (ProductProvider.getNumericProductAttributes, equation_table_field.dart).
   *  Any category's attributes are accepted, since the device is picked per
   *  row. A real column with the same key wins. */
  private productAttributeColumns(
    tableColumns: ExpressionColumnInfo[],
  ): ExpressionColumnInfo[] {
    if (!tableColumns.some((col) => col.key === "deviceProductUuid")) {
      return [];
    }
    const keys = new Set(tableColumns.map((col) => col.key));
    const attributes: ExpressionColumnInfo[] = [];
    for (const category of this.allProductCategories() ?? []) {
      for (const attr of category.productCategoryAttributes ?? []) {
        const numeric =
          +attr.productAttributeType === ProductAttributeType.INTEGER ||
          +attr.productAttributeType === ProductAttributeType.DECIMAL;
        if (numeric && attr.key && !keys.has(attr.key)) {
          keys.add(attr.key);
          attributes.push({ key: attr.key, type: TableFieldType.DECIMAL });
        }
      }
    }
    return attributes;
  }

  /** How much of a DROPDOWN column's option list has a numeric value
   *  assigned - `hasAny` is what makes the column usable in an expression
   *  at all, `allMapped` is whether *every* option is covered (picking an
   *  unmapped option would silently break the expression on-device). */
  private dropdownNumericCoverage(column: FormGroup): {
    hasAny: boolean;
    allMapped: boolean;
  } {
    const options = ((column.controls["values"] as FormArray)?.controls ?? [])
      .map((c) => (c.value as string | null) ?? "")
      .filter((v) => v !== "");
    const raw = column.controls["valueMap"]?.value as string | null;
    if (options.length === 0 || !raw) {
      return { hasAny: false, allMapped: false };
    }
    let map: Record<string, number | null>;
    try {
      map = JSON.parse(raw) as Record<string, number | null>;
    } catch {
      return { hasAny: false, allMapped: false };
    }
    const mappedCount = options.filter(
      (opt) => map[opt] != null && !isNaN(+map[opt]),
    ).length;
    return { hasAny: mappedCount > 0, allMapped: mappedCount === options.length };
  }

  /** Non-blocking heads-up for the currently-typed expression (e.g. a
   *  DROPDOWN column referenced without every option mapped to a number). */
  expressionWarnings(): string[] {
    return validateExpression(this.expForm.controls.expression.value, this.availableColumns())
      .warnings;
  }

  itemFormDisableControls() {
    const itemForm = this.itemForm();
    if (
      this.isDeviceTableRequiredColumn() ||
      itemForm.get("formTableAggregateType").value ===
        FormTableAggregateType.CNT ||
      itemForm.get("formTableAggregateType").value ===
        FormTableAggregateType.AVG
    ) {
      itemForm.get("required")?.disable();
      itemForm.get("type")?.disable();
      itemForm.get("defaultValue")?.enable();
    } else {
      itemForm.get("required")?.enable();
      itemForm.get("type")?.enable();
      if (itemForm.get("type").value === TableFieldType.DROPDOWN) {
        this.setDefaultOptionDisabledWhenNoInputs();
      } else {
        itemForm.get("defaultValue")?.enable();
      }
    }
  }

  itemFormSetValues() {
    const index = this.index();
    // sort is set when the column is created - this runs on every expand / view switch
    let colName = "col_" + String.fromCharCode("a".charCodeAt(0) + index);
    if (this.tableType() === FormTableType.AGGREGATE) {
      colName = "dim_" + colName;
    }
    const itemForm = this.itemForm();
    if (
      itemForm.controls.columnKey.value == null ||
      itemForm.controls.columnKey.value === ""
    ) {
      itemForm.controls.columnKey.setValue(colName);
    }
    this.selectedType = +itemForm.controls.type.value;

    if (
      itemForm.controls.type.value === +TableFieldType.EXPRESSION &&
      (itemForm.controls.values as FormArray).length > 0
    ) {
      this.expForm.setValue({
        expression: (itemForm.controls.values as FormArray).controls[0].value,
      });
    }
  }

  itemFormSetValidator() {
    this.itemForm().setValidators([
      this.formHiddenAndDefaultValidator,
      this.expressionColumnValidator,
    ]);
    this.itemForm().updateValueAndValidity();
  }

  formHiddenAndDefaultValidator(form: AbstractControl) {
    if (
      form.get("visible")?.value === false &&
      form.get("required")?.value === true &&
      form.get("defaultValue")?.value == null
    ) {
      return { validRequired: true };
    }
    return null;
  }

  /**
   * Mirrors expForm's own expressionSyntaxValidator so an invalid formula
   * also surfaces at the column/table level (the red error-outline icon
   * used everywhere else), not just as a mat-error under the input.
   */
  private expressionColumnValidator = (
    form: AbstractControl,
  ): ValidationErrors | null => {
    if (+form.get("type")?.value !== TableFieldType.EXPRESSION) {
      return null;
    }
    const values = form.get("values") as FormArray;
    const expression = values?.length > 0 ? (values.at(0).value as string) : "";
    const { errors } = validateExpression(expression, this.availableColumns());
    return errors.length > 0 ? { expressionSyntax: errors } : null;
  };

  delete() {
    this.deleteItem.emit(this.index());
  }

  changeType(value) {
    this.selectedType = value;
    const array = this.itemForm().controls.values as FormArray;
    const defaultValue = this.itemForm().controls.defaultValue as FormControl;
    array.clear();
    switch (value as TableFieldType) {
      case TableFieldType.INTEGER:
        defaultValue.setValidators(Validators.pattern(INTEGER_PATTERN));
        break;
      case TableFieldType.DECIMAL:
        defaultValue.setValidators(Validators.pattern(DECIMAL_PATTERN));
        break;
      case TableFieldType.DROPDOWN:
      case TableFieldType.RADIO:
      case TableFieldType.EXPRESSION:
        array.setValidators(Validators.required);
        break;
      default:
        array.setValidators(null);
        defaultValue.setValidators(null);
        break;
    }
    defaultValue.updateValueAndValidity();
    array.updateValueAndValidity();
  }

  getValues() {
    return (this.itemForm().get("values") as FormArray).controls;
  }

  onAddButtonClick() {
    if (this.inputTmp.length > 0) {
      const array = this.itemForm().controls.values as FormArray;
      array.push(new FormControl(this.inputTmp));
      this.inputTmp = "";
      this.setDefaultOptionDisabledWhenNoInputs();
    }
  }

  setDefaultOptionDisabledWhenNoInputs() {
    const vals = (this.itemForm().controls.values as FormArray).length;
    if (vals > 0) {
      this.itemForm().get("defaultValue")?.enable();
    } else {
      this.itemForm().get("defaultValue")?.disable();
    }
  }

  onTextChange(event) {
    this.inputTmp = event.target.value;
  }

  onExprChange(event) {
    const array = this.itemForm().controls.values as FormArray;
    array.clear();
    if (event.target.value !== "") {
      array.push(new FormControl(event.target.value));
    }
  }

  isDeviceTableColumn(): boolean {
    const val = this.itemForm().controls.columnKey.value;
    // device attribute columns can be edited and removed like any other column
    if (val.startsWith(DEVICE_ATTRIBUTE_COLUMN_PREFIX)) {
      return false;
    }
    return val.indexOf("device") !== -1 || val.indexOf("product") !== -1;
  }

  isDeviceTableRequiredColumn(): boolean {
    const val = this.itemForm().controls.columnKey.value;
    return val === "productCategoryUuid" || val === "deviceProductUuid";
  }

  isFixedAggregateType(itemForm: FormGroup) {
    return (
      this.tableType() === FormTableType.AGGREGATE &&
      (+itemForm.controls.formTableAggregateType.value ===
        FormTableAggregateType.CNT ||
        +itemForm.controls.formTableAggregateType.value ===
          FormTableAggregateType.AVG ||
        itemForm.controls.formTableAggregateType.value == null ||
        itemForm.controls.aggregate == null ||
        itemForm.controls.aggregate.value === false)
    );
  }

  changeVisible(itemForm: FormGroup) {
    itemForm.controls.visible.setValue(!itemForm.controls.visible.value);
  }

  aggregateSelected(itemForm: FormGroup) {
    const colArr = itemForm.controls.columnKey.value.split("_");
    const colName = colArr.slice(1, colArr.length).join("_");
    if (itemForm.controls.aggregate.value) {
      let prefix =
        itemForm.controls.formTableAggregateType.value == null ||
        isNaN(itemForm.controls.formTableAggregateType.value)
          ? "agr"
          : TableAggregateType[
              itemForm.controls.formTableAggregateType.value
            ]?.toLowerCase();
      if (
        itemForm.controls.defaultValue.value != null &&
        itemForm.controls.defalutValue?.value !== ""
      ) {
        prefix = prefix + itemForm.controls.defaultValue.value;
      }
      prefix += "_";
      itemForm.controls.formTableAggregateType.setValidators([
        Validators.required,
      ]);
      itemForm.controls.columnKey.setValue(prefix + colName);
    } else {
      itemForm.controls.type.setValue(TableFieldType.TEXT);
      itemForm.controls.formTableAggregateType.setValue(null);
      itemForm.controls.formTableAggregateType.setValidators([]);
      let prefix = "dim";
      prefix +=
        itemForm.controls.defaultValue.value == null
          ? "_"
          : itemForm.controls.defaultValue.value + "_";
      itemForm.controls.columnKey.setValue(prefix + colName);
    }
    itemForm.controls.formTableAggregateType.updateValueAndValidity();
  }

  onAggregateTypeChanged(itemForm: FormGroup, $event: MatSelectChange) {
    const selAggr = $event.value;
    const colArr = itemForm.controls.columnKey.value.split("_");
    const colName = colArr.slice(1, colArr.length).join("_");
    let prefix =
      itemForm.controls.formTableAggregateType.value == null ||
      isNaN(itemForm.controls.formTableAggregateType.value)
        ? "agr"
        : TableAggregateType[
            itemForm.controls.formTableAggregateType.value
          ].toLowerCase();
    prefix +=
      itemForm.controls.defaultValue.value == null
        ? "_"
        : itemForm.controls.defaultValue.value + "_";
    if (
      +selAggr === FormTableAggregateType.CNT ||
      +selAggr === FormTableAggregateType.CND
    ) {
      itemForm.controls.type.setValue(TableFieldType.INTEGER);
      itemForm.controls.type.disable();
    } else {
      if (+selAggr === FormTableAggregateType.AVG) {
        itemForm.controls.type.setValue(TableFieldType.DECIMAL);
        itemForm.controls.type.disable();
      } else {
        itemForm.controls.type.enable();
      }
    }
    itemForm.controls.columnKey.setValue(prefix + colName);
    itemForm.controls.type.updateValueAndValidity();
  }

  onDefaultProductCategoryChange(categDefValueUuid: string) {
    const category = this.productCategories.find(
      (pcs) => pcs.uuid === categDefValueUuid,
    );
    if (category) {
      this.addDeviceAttributeColumns(category);
    }
  }

  /**
   * Adds a "devAttr_<key>" column for each attribute of the category that technicians fill in per device,
   * unless the table already has it. Fixed product attributes need no column: formulas use them by key.
   */
  private addDeviceAttributeColumns(category: ProductCategory) {
    const columns = this.itemForm().parent as FormArray;
    if (!columns) {
      return;
    }
    const existingKeys = new Set(
      columns.controls.map((column) => column.get("columnKey")?.value),
    );
    let sort = Math.max(
      0,
      ...columns.controls.map((column) => +column.get("sort")?.value || 0),
    );
    [...(category.productCategoryAttributes ?? [])]
      .filter((attribute) => attribute.scope === ProductAttributeScope.DEVICE)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .forEach((attribute) => {
        const columnKey = DEVICE_ATTRIBUTE_COLUMN_PREFIX + attribute.key;
        if (existingKeys.has(columnKey)) {
          return;
        }
        sort += 1;
        columns.push(
          this.fb.group(
            new TableItemForm(
              new TableItem(
                attribute.name,
                this.deviceAttributeColumnType(attribute.productAttributeType),
                columnKey,
                sort,
                1,
                null,
                false,
                true,
              ),
            ),
          ),
        );
      });
  }

  private deviceAttributeColumnType(type: ProductAttributeType): TableFieldType {
    switch (type) {
      case ProductAttributeType.BOOLEAN:
        return TableFieldType.BOOLEAN;
      case ProductAttributeType.INTEGER:
        return TableFieldType.INTEGER;
      case ProductAttributeType.DECIMAL:
        return TableFieldType.DECIMAL;
      default:
        // tables have no timestamp column; such values are entered as text, like on products
        return TableFieldType.TEXT;
    }
  }

  openConditionDialog() {
    const itemForm = this.itemForm();
    if (itemForm.controls.type.value === +TableFieldType.CONDITION) {
      let condition: TableFieldCondition | null = null;
      if ((itemForm.controls.values as FormArray).length > 0) {
        const b64Condition = (itemForm.controls.values as FormArray).controls[0]
          .value;
        const dataJson = Buffer.from(b64Condition, "base64").toString("utf-8");
        condition = JSON.parse(dataJson) as TableFieldCondition;
      }
      const data: TableFieldConditionDialogData = {
        condition,
        columns: this.availableColumns(),
      };
      const dialogRef = this.dialog.open(TableFieldConditionDialogComponent, {
        width: "560px",
        maxWidth: "95vw",
        data,
      });
      dialogRef.afterClosed().subscribe((dt: TableFieldCondition) => {
        if (dt != null) {
          const dtJson = JSON.stringify(dt);
          const base64Condition = Buffer.from(dtJson, "utf-8").toString(
            "base64",
          );
          console.log(base64Condition);
          const array = this.itemForm().controls.values as FormArray;
          array.clear();
          array.push(new FormControl(base64Condition));
        }
      });
    }
  }

  protected editValues(): void {
    const valuesArray = this.itemForm().get("values") as FormArray<
      FormControl<string | null>
    >;
    const valueMapControl = this.itemForm().get("valueMap");
    const defaultValueControl = this.itemForm().get("defaultValue");

    let parsedValueMap: Record<string, number | null> = {};

    try {
      parsedValueMap = valueMapControl?.value
        ? JSON.parse(valueMapControl.value)
        : {};
    } catch {
      parsedValueMap = {};
    }

    const currentValues: DropdownValueItem[] = valuesArray.controls.map(
      (control) => {
        const value = control.value ?? "";

        return {
          value,
          numericValue: parsedValueMap[value] ?? null,
        };
      },
    );

    const dialogRef = this.dialog.open<
      DropdownEditValuesDialogComponent,
      { values: DropdownValueItem[] },
      DropdownValueItem[] | undefined
    >(DropdownEditValuesDialogComponent, {
      width: "680px",
      maxWidth: "95vw",
      data: {
        values: currentValues,
        numericValueDisabled: this.selectedType === +TableFieldType.RADIO,
      } as DropdownEditValuesDialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }

      valuesArray.clear();

      const nextValueMap: Record<string, number | null> = {};

      result.forEach((item) => {
        valuesArray.push(new FormControl(item.value));
        nextValueMap[item.value] = item.numericValue;
      });

      valueMapControl?.setValue(JSON.stringify(nextValueMap));

      const currentDefaultValue = defaultValueControl?.value;

      if (!defaultValueControl) {
        return;
      }

      if (
        currentDefaultValue === null ||
        currentDefaultValue === undefined ||
        currentDefaultValue === ""
      ) {
        return;
      }

      const defaultStillExists = result.some(
        (item) => item.value === currentDefaultValue,
      );

      if (defaultStillExists) {
        return;
      }

      defaultValueControl.setValue(result[0]?.value ?? null);
    });
  }
}
